"use client";

import { useTranslations } from "next-intl";
import { useState, type ReactNode, type RefObject, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogTrigger, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import type { ActionState } from "./action-state.type";
import { ActionForm } from "./action-form";
import { cn } from "@/lib/utils";

type ModalProps = {
  trigger: string;
  /** `default` for the main button of a page (such as Add), `outline` (the usual) for a row action. */
  triggerVariant?: "default" | "outline" | "destructive";
  /** For a table row: shows only this icon (the `trigger` words become its name and tooltip) to save space. */
  triggerIcon?: ReactNode;
  /** A small icon (`<Crown />`) shown in a tinted square before the title. */
  icon?: ReactNode;
  /** Shown between the icon and the title, such as the picture of the person the window is about. */
  leading?: ReactNode;
  title: string;
  description: string;
  submit: string;
  action: (previous: ActionState, data: FormData) => Promise<ActionState>;
  /** md: about 36rem (few fields), lg: 48rem (the usual), xl: 64rem (many sections). */
  size?: "md" | "lg" | "xl";
  /** A window that keeps its full height (85% of the screen) even with little content, so it does not jump in size. */
  tall?: boolean;
  /** For a form opened from elsewhere (a row's menu): the window's open state, kept by the caller; no button is shown then. */
  controlled?: { open: boolean; onOpenChange: (open: boolean) => void };
  /** Asks "Are you sure?" in the site's own window before sending (see `ActionForm`). */
  confirm?: string;
  /** The form's fields, built on the server, usually in `ModalSection`s. */
  children: ReactNode;
};

/** The window every form opens in, with the same three parts everywhere. Header: a tinted band with the icon, the title and the description. Content: the fields, in as many columns as fit, with a numbered list of its sections down the side (`SectionNav`, from two sections, on larger screens); it is the only part that scrolls. Footer: a bar with Cancel and the main button at the end. It closes by itself once the change was saved. */
export function FormModal({ trigger, triggerVariant = "outline", triggerIcon, icon, leading, title, description, submit, action, size = "lg", tall, controlled, confirm, children }: ModalProps) {
  const t = useTranslations("confirm");
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlled?.open ?? ownOpen;
  const setOpen = controlled?.onOpenChange ?? setOwnOpen;
  const saveThenClose = useSaveThenClose(action, () => setOpen(false));
  const cancel = <DialogClose render={<Button type="button" variant="outline" />}>{t("cancel")}</DialogClose>;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && <DialogTrigger render={<Button type="button" variant={triggerVariant} size={triggerIcon ? "icon-sm" : "sm"} aria-label={triggerIcon ? trigger : undefined} title={triggerIcon ? trigger : undefined} />}>{triggerIcon ?? trigger}</DialogTrigger>}
      <ModalContent size={size} tall={tall}>
        <ModalHeader icon={icon} leading={leading} title={title} description={description} />
        <ActionForm action={saveThenClose} submit={submit} confirm={confirm} className="min-h-0 flex-1 flex-col flex-nowrap items-stretch gap-0" barClassName={MODAL_FOOTER_CLASS} footer={cancel}>
          <ModalBody>{children}</ModalBody>
        </ActionForm>
      </ModalContent>
    </Dialog>
  );
}

const WIDTHS = { md: "sm:max-w-xl", lg: "sm:max-w-3xl", xl: "sm:max-w-5xl" } as const;

/** The frame of every window: no padding of its own, up to 92% of the screen high, and one of three widths (md about 36rem, lg 48rem, xl 64rem). The header, body and footer parts go inside it. */
export function ModalContent({ size = "lg", tall, children }: { size?: keyof typeof WIDTHS; tall?: boolean; children: ReactNode }) {
  return <DialogContent className={cn("flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0", tall && "h-[85vh]", WIDTHS[size])}>{children}</DialogContent>;
}

/** The middle of every window: the numbered list of its sections down the side (`SectionNav`) and the content, which is the only part that scrolls. */
export function ModalBody({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-0 flex-1 md:grid-cols-[auto_minmax(0,1fr)]">
      <SectionNav />
      <div className="flex min-h-0 flex-col gap-6 overflow-y-auto px-6 py-5 [counter-reset:modal-section]">{children}</div>
    </div>
  );
}

/** The tinted band at the top of every window: an optional icon in a tinted square, an optional `leading` element (such as the picture of the person the window is about) before the title, the title and the one-line description, and an optional `aside` (a badge) at the end. */
export function ModalHeader({ icon, leading, title, description, aside }: { icon?: ReactNode; leading?: ReactNode; title: string; description: string; aside?: ReactNode }) {
  return (
    <header className="flex items-center gap-3 border-b bg-muted/40 py-4 ps-6 pe-14">
      {icon && <span aria-hidden className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-5">{icon}</span>}
      {leading}
      <div className="flex min-w-0 flex-col gap-1"><DialogTitle className="truncate text-base">{title}</DialogTitle><DialogDescription>{description}</DialogDescription></div>
      {aside && <div className="ms-auto shrink-0">{aside}</div>}
    </header>
  );
}

/** The bar at the bottom of a window that is not a form: its buttons, at the end. */
export function ModalFooter({ children }: { children: ReactNode }) {
  return <div className={MODAL_FOOTER_CLASS}>{children}</div>;
}

/** The look of the bar at the bottom of every window (buttons at the end). */
export const MODAL_FOOTER_CLASS = "flex items-center justify-end gap-2 border-t bg-muted/40 px-6 py-3";

/** The numbered list of a window's sections, down the side: each entry jumps to its section (`ModalSection`) in the scrolling content next to it, and the one in view is marked. It reads the headings from that content, so nothing has to be listed twice; with fewer than two sections it shows nothing. Hidden on a phone. */
function SectionNav() {
  const nav = useRef<HTMLElement>(null);
  const { items, current } = useModalSections(nav);
  return (
    <nav ref={nav} aria-label="sections" className={cn("hidden flex-col gap-1 border-e bg-muted/30 p-3", items.length > 1 && "md:flex")}>
      {items.map((item, i) => (
        <button key={item.title} type="button" onClick={() => item.element.scrollIntoView({ behavior: "smooth", block: "start" })} className={cn("flex items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors hover:bg-accent", i === current && "bg-accent font-medium")}>
          <span className={cn("flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs text-primary", i === current && "bg-primary text-primary-foreground")}>{i + 1}</span>
          <span className="truncate">{item.title}</span>
        </button>
      ))}
    </nav>
  );
}

/** The sections (`ModalSection`) of the scrolling content that follows `nav`, as `{ title, element }` in order, and the index of the one in view. */
function useModalSections(nav: RefObject<HTMLElement | null>) {
  const [items, setItems] = useState<{ title: string; element: Element }[]>([]);
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const scroller = nav.current?.nextElementSibling;
    if (!scroller) return;
    let seen: IntersectionObserver | undefined;
    let sections: Element[] = [];
    // The list is read again when the content gains or loses a section (a part shown only for some choice).
    const scan = () => {
      const next = [...scroller.querySelectorAll("section[data-modal-section]")];
      if (seen && next.length === sections.length && next.every((s, i) => s === sections[i])) return;
      seen?.disconnect();
      sections = next;
      setItems(sections.map((element) => ({ title: element.querySelector("h3")?.textContent ?? "", element })));
      seen = new IntersectionObserver((entries) => {
        const top = entries.find((e) => e.isIntersecting);
        if (top) setCurrent(sections.indexOf(top.target));
      }, { root: scroller, rootMargin: "0px 0px -70% 0px" });
      sections.forEach((s) => seen?.observe(s));
    };
    scan();
    const watch = new MutationObserver(scan);
    watch.observe(scroller, { childList: true, subtree: true });
    return () => { watch.disconnect(); seen?.disconnect(); };
  }, [nav]);
  return { items, current };
}

type Action = (previous: ActionState, data: FormData) => Promise<ActionState>;

/** Wraps a form action so that, once it reports the change was saved, the window holding the form is closed (`close`). Refusals keep it open, with the reason shown, and so does a result that only says something (`note`, such as a successful test). */
function useSaveThenClose(action: Action, close: () => void): Action {
  return useCallback(async (previous, data) => {
    const state = await action(previous, data);
    if (state.done && (!state.note || state.note === "folder-failed")) close();
    return state;
  }, [action, close]);
}
